// PentestButton
// --------------
// Outline pill that drives the test lifecycle for a single webapp:
// Pentest Now (idle) → Starting… (provisioning) → Stop Pentest
// (running) → Stopping… (teardown) → Pentest Now.
//
// Lives in the WebApplication page header next to Report and Settings
// so the primary verb sits with the other page-level actions instead
// of buried inside the Security Status card. The rotating "Test in
// progress" status message stays in the Security Status card header
// — it describes the *state* of testing (which is contextual to that
// card); this button is the *action* (which is page-level).
//
// Phase-driven: the button renders purely from the app's single live-run
// signal (livePhase, from utils/liveTestState.ts). Because that signal is
// set optimiztically at the source (startTest → provisioning, stopTest →
// stopping) the button shows "Starting…" / "Stopping…" the moment the
// user clicks — with no separate optimiztic-gap reconstruction to keep in
// sync (the old isOptimiztic* logic is gone).

import React from "react";
import { Play, Square } from "lucide-react";
import { LivePhase } from "@/utils/liveTestState";

interface PentestButtonProps {
  // The app's live-run phase: "provisioning" (Starting…), "running"
  // (Stop Pentest), "stopping" (Stopping…), or "none" (Pentest Now).
  livePhase: LivePhase;
  onClick: () => void;
  // True when this app's license blocks a new pentest (no runs left /
  // access window ended / license inactive). PentestCard owns the
  // reason banner; this prop just disables the idle Pentest Now
  // affordance so the click can't proceed. Has no effect while a run is
  // in flight — stopping must remain available regardless.
  disabledByLimit?: boolean;
}

export default function PentestButton({
  livePhase,
  onClick,
  disabledByLimit = false,
}: PentestButtonProps) {
  const isProvisioning = livePhase === "provisioning";
  const isStopping = livePhase === "stopping";
  const isRunning = livePhase === "running";

  // Outline pill style — border + colored text on white, no fill.
  // Shared chrome across all four states; only the border + text color
  // and the icon change, so the button reads as one element changing
  // state rather than four different buttons. min-w-[140px] absorbs the
  // widest label ("Stop Pentest", 12 chars) so the button doesn't
  // resize between transitions.
  const baseClass =
    "inline-flex items-center justify-center gap-1.5 min-w-[140px] rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-100";

  // Starting / Stopping states: no spinner on the button — the spinner
  // sits on the rotating status message below, where it belongs with
  // the descriptive copy. Button stays a disabled gray pill with just
  // the label + ellipsis, which carries the "in progress" sense on
  // its own without doubling up the animation.
  if (isProvisioning) {
    return (
      <button
        type="button"
        disabled
        title="Test environment is being set up — typically a few minutes."
        className={`${baseClass} border-gray-300 text-gray-400 cursor-not-allowed`}
      >
        Starting…
      </button>
    );
  }

  if (isStopping) {
    return (
      <button
        type="button"
        disabled
        title="Test environment is tearing down — typically 3–7 minutes."
        className={`${baseClass} border-gray-300 text-gray-400 cursor-not-allowed`}
      >
        Stopping…
      </button>
    );
  }

  if (isRunning) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClass} border-red-500 text-red-600 hover:bg-red-50 hover:border-red-600`}
      >
        <Square className="h-3.5 w-3.5" />
        Stop Pentest
      </button>
    );
  }

  if (disabledByLimit) {
    return (
      <button
        type="button"
        disabled
        title="This app's license can't start a new pentest right now — see the note above the button."
        className={`${baseClass} border-gray-300 text-gray-400 cursor-not-allowed`}
      >
        <Play className="h-3.5 w-3.5" />
        Pentest Now
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClass} border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600`}
    >
      <Play className="h-3.5 w-3.5" />
      Pentest Now
    </button>
  );
}
